import { Button } from '@1trading/react';
import { useRef } from 'react';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FUTURES_REVIEW_KEY_PREFIX, type FuturesReviewAlert, setFuturesManualReviewOutcome } from '@/services/api/compliance-alerts';

interface FuturesAppropriatenessReviewActionsProps {
  alert: FuturesReviewAlert;
  onClose: () => void;
  /** Dismisses the alert with the same justification (second, non-atomic call) */
  onDismiss: (justification: string) => Promise<void>;
}

export const FuturesAppropriatenessReviewActions = ({ alert, onClose, onDismiss }: FuturesAppropriatenessReviewActionsProps) => {
  // Legacy UI-layer validation (the API only requires a non-empty key) — keep it
  const isKeyValid = typeof alert.key === 'string' && alert.key.startsWith(FUTURES_REVIEW_KEY_PREFIX);
  // Outcome + dismiss are two non-atomic calls: on a dismiss-failure retry the
  // (already successful) outcome call must not fire again.
  const isOutcomeApplied = useRef(false);

  const handleOutcome = (outcome: 'PASSED' | 'FAILED') => {
    isOutcomeApplied.current = false;
    return confirmAction({
      title: outcome === 'PASSED' ? 'Pass Assessment' : 'Fail Assessment',
      description: `Proceeding with this action will set the futures appropriateness test (${alert.key}) result to ${outcome} and dismiss the alert with ID ${alert.alert_id}. Your action here will be logged and will be auditable.`,
      actionLabel: outcome === 'PASSED' ? 'Pass Assessment' : 'Fail Assessment',
      destructive: outcome === 'FAILED',
      onConfirm: async (justification) => {
        if (!isOutcomeApplied.current) {
          await setFuturesManualReviewOutcome(alert.pid, { key: alert.key, outcome, comment: justification });
          isOutcomeApplied.current = true;
        }
        await onDismiss(justification);
      },
    });
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {isKeyValid ? null : <p className="text-sm text-red-500">This alert has an invalid assessment key ({String(alert.key)}) — outcome actions are disabled.</p>}
      <div className="flex justify-end gap-2">
        <Button onClick={onClose} type="button" variant="outline">
          Close
        </Button>
        <Button disabled={!isKeyValid} onClick={() => handleOutcome('PASSED')} type="button" variant="primary">
          Pass Assessment
        </Button>
        <Button disabled={!isKeyValid} onClick={() => handleOutcome('FAILED')} type="button" variant="destructive">
          Fail Assessment
        </Button>
      </div>
    </div>
  );
};
FuturesAppropriatenessReviewActions.displayName = 'FuturesAppropriatenessReviewActions';
